import { alignText, isLineEnd } from "./align-text";

import type { LineBox, TextLine } from "@/models/Viewer";

// Fraction of a character's height that it may overlap the previous character (kerning, overlapping OCR glyphs)
// and still count as moving forward. Wrapping to a new line jumps back much further.
const BACKWARD_TOLERANCE = 0.5;

function round(value: number) {
  return +value.toFixed(2);
}

/** Grows the box of `line` to cover the rectangle `[x, y, width, height]`. */
function extendBounds(line: LineBox, x: number, y: number, width: number, height: number) {
  const left = Math.min(line.x, x);
  const top = Math.min(line.y, y);
  line.width = round(Math.max(line.x + line.width, x + width) - left);
  line.height = round(Math.max(line.y + line.height, y + height) - top);
  line.x = left;
  line.y = top;
}

function isVerticallyOnLine(line: TextLine, y: number, height: number) {
  const centre = y + height / 2;
  const lineCentre = line.y + line.height / 2;
  return (centre >= line.y && centre <= line.y + line.height) || (lineCentre >= y && lineCentre <= y + height);
}

function movesBackward(line: TextLine, x: number, height: number) {
  const [previousX, , previousWidth] = line.rectangles[line.rectangles.length - 1];
  return x < previousX + previousWidth - height * BACKWARD_TOLERANCE;
}

/**
 * Groups a page's characters into visual lines. `rectangles` holds one `[x, y, width, height]` per character and
 * `text` holds the characters in the same order. The two can drift apart: a rectangle with no character (such as a
 * tick box) shifts every later character, so `alignText` pairs them line by line, and line ends are found from the
 * rectangles, never from `text`. Each line holds the text of its rectangles (`characters`, with `""` for a rectangle
 * with no character) and the index of its first character in `text` (`startIndex`), so the lines tile `text`.
 * Cells or columns on the same visual row arrive as separate text lines, so a line end can sit mid-row. A line end
 * never starts a line and stays on the line before it. Any other rectangle starts a new line when it is not vertically
 * on the current line (neither its vertical centre nor the line's falls inside the other's box) or moves backward
 * horizontally. Each line's rectangles stay contiguous and in page order, and its box is the bounding box of its
 * rectangles other than line ends, unless a line end is its only rectangle. The rectangles given are held by the lines
 * as they are, never copied or changed.
 */
export function groupTextLines(text: string, rectangles: number[][]): TextLine[] {
  const characters = alignText(text, rectangles);
  const textLines: TextLine[] = [];
  let currentLine: TextLine | null = null;
  // The current line holds nothing but line ends so far, so its box is not the box of a character yet
  let onlyLineEnds = false;
  let startIndex = 0;

  for (let index = 0; index < rectangles.length; index++) {
    const r = rectangles[index];
    const [x, y, width, height] = r;
    const t = characters[index];
    const lineEnd = isLineEnd(r);

    if (
      currentLine == null ||
      (!lineEnd && (!isVerticallyOnLine(currentLine, y, height) || movesBackward(currentLine, x, height)))
    ) {
      currentLine = { x, y, width, height, rectangles: [r], characters: [t], startIndex, text: t };
      textLines.push(currentLine);
      onlyLineEnds = lineEnd;
    } else {
      currentLine.rectangles.push(r);
      currentLine.characters.push(t);
      currentLine.text += t;

      if (!lineEnd) {
        if (onlyLineEnds) {
          currentLine.x = x;
          currentLine.y = y;
          currentLine.width = width;
          currentLine.height = height;
        } else {
          extendBounds(currentLine, x, y, width, height);
        }
        onlyLineEnds = false;
      }
    }

    startIndex += t.length;
  }

  return textLines;
}
